import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../../context/GameContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiRefreshCw, FiClock, FiSun, FiAward, FiBookOpen, FiZap } = FiIcons;

function MultiplicationIslandGame() {
  const navigate = useNavigate();
  const { state, dispatch } = useGame();
  
  // Game States: 'menu', 'study', 'countdown', 'playing', 'result'
  const [gameState, setGameState] = useState('menu');
  const [selectedTable, setSelectedTable] = useState(null); // 1-10 or 'mixed'
  const [timeLeft, setTimeLeft] = useState(60);
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [feedback, setFeedback] = useState(null); // 'correct' or 'wrong'
  const [studyCardFlipped, setStudyCardFlipped] = useState(null);

  // Island Theme Colors
  const themeColors = {
    bg: "bg-gradient-to-br from-cyan-400 via-blue-400 to-blue-600",
    card: "bg-white",
    accent: "text-cyan-600",
    button: "from-yellow-400 to-orange-500"
  };

  // Generate a multiplication question
  const generateQuestion = (table) => {
    let num1, num2;
    if (table === 'mixed') {
      num1 = Math.floor(Math.random() * 10) + 1;
      num2 = Math.floor(Math.random() * 10) + 1;
    } else {
      num1 = table;
      num2 = Math.floor(Math.random() * 10) + 1;
    }

    const correct = num1 * num2;
    
    // Generate wrong answers that are plausible (close to correct answer)
    const wrongAnswers = new Set();
    while (wrongAnswers.size < 3) {
      const offset = Math.floor(Math.random() * 10) - 5;
      const wrong = correct + offset;
      if (wrong > 0 && wrong !== correct) {
        wrongAnswers.add(wrong);
      }
    }

    // Sometimes add a "tricky" wrong answer (e.g., right numbers, wrong operation)
    if (Math.random() > 0.5) {
      wrongAnswers.add(num1 + num2); // Addition answer
    }

    const allAnswers = [correct, ...Array.from(wrongAnswers).slice(0, 3)].sort(() => Math.random() - 0.5);

    return {
      num1,
      num2,
      correct,
      options: allAnswers
    };
  };

  // Timer Logic
  useEffect(() => {
    let timer;
    if (gameState === 'playing' && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
    } else if (timeLeft === 0 && gameState === 'playing') {
      finishGame();
    }
    return () => clearInterval(timer);
  }, [gameState, timeLeft]);

  // Game Logic
  const startGame = (table) => {
    setSelectedTable(table);
    setGameState('countdown');
    setTimeout(() => {
      setGameState('playing');
      setTimeLeft(60);
      setScore(0);
      setCombo(0);
      setCurrentQuestion(generateQuestion(table));
    }, 3000);
  };

  const handleAnswer = (answer) => {
    if (gameState !== 'playing') return;

    if (answer === currentQuestion.correct) {
      // Correct
      const timeBonus = Math.ceil(timeLeft / 10); // Bonus for speed
      const comboBonus = Math.min(combo * 2, 20); // Bonus for streaks
      const points = 10 + timeBonus + comboBonus;
      
      setScore((prev) => prev + points);
      setCombo((prev) => prev + 1);
      setFeedback('correct');
      setTimeout(() => setFeedback(null), 500);
    } else {
      // Wrong
      setCombo(0);
      setFeedback('wrong');
      dispatch({ type: 'ADD_WRONG_ANSWER' });
      setTimeout(() => setFeedback(null), 500);
    }
    
    // Next Question immediately
    setCurrentQuestion(generateQuestion(selectedTable));
  };

  const finishGame = () => {
    setGameState('result');
    // Calculate rewards
    if (score > 0) {
      const tokens = Math.floor(score / 10);
      dispatch({ type: 'ADD_SCORE', payload: score });
      
      // Auto-complete quest if score is good
      if (score > 200) {
        dispatch({ 
          type: 'GAME_COMPLETED', 
          payload: { 
            questId: 'multiplication-island',
            bonusTokens: 20
          } 
        });
      }
    }
  };

  const resetGame = () => {
    setGameState('menu');
    setTimeLeft(60);
    setScore(0);
    setCombo(0);
  };

  // Render Functions
  const renderMenu = () => (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-4xl mx-auto text-center"
    >
      <div className="bg-white/90 backdrop-blur-sm rounded-3xl p-8 shadow-2xl border-4 border-cyan-200">
        <div className="text-6xl mb-4">🏝️</div>
        <h1 className="text-4xl font-kid text-cyan-700 mb-2">Multiplication Island</h1>
        <p className="text-cyan-600 font-comic text-lg mb-8">Master the tables to survive the island!</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Study Mode */}
          <div className="bg-blue-50 rounded-2xl p-6 border-2 border-blue-200">
            <h2 className="text-2xl font-kid text-blue-600 mb-4 flex items-center justify-center gap-2">
              <SafeIcon icon={FiBookOpen} /> Study Hut
            </h2>
            <p className="font-comic text-blue-500 mb-4">Memorize your tables before the challenge!</p>
            <div className="grid grid-cols-5 gap-2">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                <button
                  key={num}
                  onClick={() => {
                    setSelectedTable(num);
                    setGameState('study');
                  }}
                  className="bg-white hover:bg-blue-100 text-blue-600 font-kid py-2 rounded-lg border border-blue-200 transition-colors"
                >
                  {num}
                </button>
              ))}
            </div>
          </div>

          {/* Speed Mode */}
          <div className="bg-orange-50 rounded-2xl p-6 border-2 border-orange-200">
            <h2 className="text-2xl font-kid text-orange-600 mb-4 flex items-center justify-center gap-2">
              <SafeIcon icon={FiZap} /> Speed Blitz
            </h2>
            <p className="font-comic text-orange-500 mb-4">Race against time! 60 seconds!</p>
            <div className="space-y-3">
              <button
                onClick={() => startGame('mixed')}
                className="w-full bg-gradient-to-r from-orange-400 to-red-500 text-white font-kid py-3 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all"
              >
                Mixed Challenge (1-10) 🌶️
              </button>
              <div className="text-sm font-comic text-orange-400">Or pick a specific table:</div>
              <div className="grid grid-cols-5 gap-2">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                  <button
                    key={num}
                    onClick={() => startGame(num)}
                    className="bg-white hover:bg-orange-100 text-orange-600 font-kid py-2 rounded-lg border border-orange-200 transition-colors"
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );

  const renderStudy = () => (
    <div className="max-w-2xl mx-auto">
      <div className="flex justify-between items-center mb-6 text-white">
        <button onClick={() => setGameState('menu')} className="bg-white/20 hover:bg-white/30 p-2 rounded-xl">
          <SafeIcon icon={FiArrowLeft} className="text-2xl" />
        </button>
        <h2 className="text-3xl font-kid">Studying {selectedTable}× Table</h2>
        <div className="w-10"></div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((num) => (
          <motion.div
            key={num}
            whileHover={{ scale: 1.05 }}
            className="h-32 perspective-1000 cursor-pointer"
            onClick={() => setStudyCardFlipped(studyCardFlipped === num ? null : num)}
          >
            <motion.div
              initial={false}
              animate={{ rotateY: studyCardFlipped === num ? 180 : 0 }}
              transition={{ duration: 0.6, type: "spring" }}
              className="w-full h-full relative preserve-3d"
              style={{ transformStyle: 'preserve-3d' }}
            >
              {/* Front */}
              <div className="absolute inset-0 backface-hidden bg-white rounded-2xl shadow-xl flex items-center justify-center border-b-4 border-cyan-200">
                <span className="text-3xl font-kid text-cyan-700">{selectedTable} × {num}</span>
              </div>
              {/* Back */}
              <div className="absolute inset-0 backface-hidden bg-gradient-to-br from-yellow-300 to-orange-400 rounded-2xl shadow-xl flex items-center justify-center rotate-y-180"
                   style={{ transform: 'rotateY(180deg)', backfaceVisibility: 'hidden' }}>
                <span className="text-4xl font-kid text-white">{selectedTable * num}</span>
              </div>
            </motion.div>
          </motion.div>
        ))}
      </div>
      
      <div className="text-center mt-8">
        <button
          onClick={() => startGame(selectedTable)}
          className="bg-white text-cyan-600 font-kid text-xl px-8 py-3 rounded-full shadow-lg hover:bg-cyan-50 transition-colors"
        >
          Ready? Start Speed Blitz! ⚡
        </button>
      </div>
    </div>
  );

  const renderCountdown = () => (
    <div className="flex items-center justify-center h-[60vh]">
      <motion.div
        animate={{ scale: [0.5, 1.5, 0], opacity: [0, 1, 0] }}
        transition={{ duration: 3, times: [0, 0.5, 1] }}
        className="text-9xl font-kid text-white drop-shadow-lg"
      >
        Ready!
      </motion.div>
    </div>
  );

  const renderPlaying = () => (
    <div className="max-w-2xl mx-auto relative">
      {/* HUD */}
      <div className="flex justify-between items-center mb-8 bg-white/20 backdrop-blur-md rounded-2xl p-4 text-white">
        <div className="flex items-center gap-2">
          <SafeIcon icon={FiClock} className="text-2xl" />
          <span className={`text-2xl font-kid ${timeLeft < 10 ? 'text-red-300 animate-pulse' : ''}`}>
            {timeLeft}s
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-2xl">🔥</span>
          <span className="text-2xl font-kid">x{combo}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-2xl">⭐</span>
          <span className="text-2xl font-kid">{score}</span>
        </div>
      </div>

      {/* Question Card */}
      <motion.div
        key={currentQuestion.correct} // Re-render animation on new question
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-3xl p-8 shadow-2xl border-4 border-cyan-200 text-center mb-8 relative overflow-hidden"
      >
        {/* Feedback Overlay */}
        <AnimatePresence>
          {feedback && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className={`absolute inset-0 flex items-center justify-center z-10 ${
                feedback === 'correct' ? 'bg-green-500/20' : 'bg-red-500/20'
              }`}
            >
              <div className={`text-8xl ${feedback === 'correct' ? 'scale-110' : 'shake'}`}>
                {feedback === 'correct' ? '✨' : '❌'}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <h2 className="text-6xl font-kid text-cyan-700 mb-2">
          {currentQuestion.num1} × {currentQuestion.num2}
        </h2>
        <div className="text-gray-400 font-comic">Quick! What's the answer?</div>
      </motion.div>

      {/* Options */}
      <div className="grid grid-cols-2 gap-4">
        {currentQuestion.options.map((opt, idx) => (
          <button
            key={idx}
            onClick={() => handleAnswer(opt)}
            className="bg-white hover:bg-cyan-50 active:bg-cyan-100 text-cyan-700 text-3xl font-kid py-6 rounded-2xl shadow-lg border-b-4 border-cyan-200 active:border-b-0 active:translate-y-1 transition-all"
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );

  const renderResult = () => (
    <motion.div 
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      className="max-w-md mx-auto bg-white rounded-3xl p-8 shadow-2xl border-4 border-cyan-200 text-center"
    >
      <div className="text-6xl mb-4">🏆</div>
      <h2 className="text-3xl font-kid text-cyan-700 mb-2">Time's Up!</h2>
      <p className="text-cyan-600 font-comic mb-6">Great memorization skills!</p>

      <div className="grid grid-cols-2 gap-4 mb-8">
        <div className="bg-cyan-50 rounded-xl p-4">
          <div className="text-sm font-comic text-cyan-500">Total Score</div>
          <div className="text-3xl font-kid text-cyan-700">{score}</div>
        </div>
        <div className="bg-orange-50 rounded-xl p-4">
          <div className="text-sm font-comic text-orange-500">Max Combo</div>
          <div className="text-3xl font-kid text-orange-700">{combo}</div>
        </div>
      </div>

      <div className="space-y-3">
        <button
          onClick={() => startGame(selectedTable)}
          className="w-full bg-cyan-500 hover:bg-cyan-600 text-white font-kid py-3 rounded-xl shadow-lg transition-colors"
        >
          Play Again
        </button>
        <button
          onClick={resetGame}
          className="w-full bg-white hover:bg-gray-50 text-cyan-600 font-kid py-3 rounded-xl border-2 border-cyan-100 transition-colors"
        >
          Choose Different Table
        </button>
        <button
          onClick={() => navigate('/dashboard')}
          className="w-full text-gray-400 font-comic hover:text-gray-600 py-2"
        >
          Back to Dashboard
        </button>
      </div>
    </motion.div>
  );

  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      exit={{ opacity: 0 }}
      className={`min-h-screen p-4 ${themeColors.bg} overflow-hidden relative`}
    >
      {/* Background Decorations */}
      <div className="absolute top-10 left-10 text-6xl opacity-20 animate-bounce-slow">🌴</div>
      <div className="absolute bottom-20 right-10 text-6xl opacity-20 animate-bounce-slow" style={{ animationDelay: '1s' }}>🦜</div>
      <div className="absolute top-20 right-20 text-4xl opacity-20 animate-pulse">☀️</div>
      <div className="absolute bottom-10 left-20 text-5xl opacity-20 animate-wiggle">🌊</div>

      {gameState === 'menu' && renderMenu()}
      {gameState === 'study' && renderStudy()}
      {gameState === 'countdown' && renderCountdown()}
      {gameState === 'playing' && renderPlaying()}
      {gameState === 'result' && renderResult()}
    </motion.div>
  );
}

export default MultiplicationIslandGame;